import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Modal, { modalStyles as m } from "./Modal";
import { phpFindCustomer } from "../data/api";
import { classifyCustomerSearch } from "../data/queries";
import { setCustomer } from "../../../Redux/features/WebRegister/cartSlice";
import { ToastifyAlert } from "../../../CommonComponents/ToastifyAlert";
import AddCustomerModal from "./AddCustomerModal";

const pick = (o, ...keys) => {
  for (const k of keys) if (o[k] != null && o[k] !== "") return o[k];
  return "";
};
const nameOf = (c) => `${pick(c, "fname", "first_name")} ${pick(c, "lname", "last_name")}`.trim() || pick(c, "name") || "Customer";
const phoneOf = (c) => pick(c, "phone_no", "phone", "mobile");
const emailOf = (c) => pick(c, "email");

// Attach a customer via the POS lookup API (App/find_customer) — search by name, phone OR email
// (the term is classified the same way POS classifies its search box).
export default function CustomerModal({ onClose }) {
  const dispatch = useDispatch();
  const attached = useSelector((st) => st.webRegisterCart.customer);
  const payments = useSelector((st) => st.webRegisterCart.payments);
  const loyaltyAmount = useSelector((st) => st.webRegisterCart.loyaltyAmount);
  const storeCreditAmount = useSelector((st) => st.webRegisterCart.storeCreditAmount);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [creating, setCreating] = useState(false);

  const doSearch = async () => {
    const term = q.trim();
    if (!term) return;
    // Route the term exactly as POS does (name / email / phone) — see classifyCustomerSearch.
    const type = classifyCustomerSearch(term);
    const params =
      type === "email" ? { email: term } : type === "name" ? { name: term } : type === "phone" ? { phone_no: term.replace(/\D/g, "") } : null;
    if (!params) return; // punctuation/bracket-only → nothing to search
    setLoading(true);
    setSearched(true);
    try {
      const res = await phpFindCustomer(params);
      const result = res && res.result;
      setRows(Array.isArray(result) ? result.filter(Boolean) : result ? [result] : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const choose = (c) => {
    dispatch(setCustomer({ ...c, _name: nameOf(c), _phone: phoneOf(c) }));
    onClose();
  };

  // Android includeLineItemSection.ivDelete guards (same as the cart chip's remove): a customer can't
  // be cleared mid-split, or while loyalty points / store credit are applied. This Remove button used
  // to clear unconditionally, bypassing those guards.
  const removeAttached = () => {
    if ((payments || []).length > 0) return ToastifyAlert("Split payment in progress", "error");
    if (Number(loyaltyAmount) !== 0 || Number(storeCreditAmount) !== 0)
      return ToastifyAlert("Remove applied loyalty point or store credit first.", "error");
    dispatch(setCustomer(null));
    onClose();
  };

  if (creating) {
    // Prefill the phone only when the operator searched by a NUMBER (not an email or a name).
    const prefillPhone = classifyCustomerSearch(q.trim()) === "phone" ? q.trim().replace(/\D/g, "") : "";
    return <AddCustomerModal initialPhone={prefillPhone} onClose={onClose} />;
  }

  return (
    <Modal title="Attach customer" onClose={onClose} width={480}>
      {attached && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: 12, background: "var(--accent-soft)", borderRadius: 10, marginBottom: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{attached._name || nameOf(attached)}</div>
            <div style={{ fontSize: 12, color: "var(--ink-2)" }}>{attached._phone || phoneOf(attached)}</div>
          </div>
          {/* Compact, non-growing button — the shared m.ghost is width:100% and would overlap the
              name/number (Varun 2026-08-27). */}
          <button style={{ ...m.ghost, width: "auto", flex: "0 0 auto", minHeight: 40, marginTop: 0, padding: "8px 18px" }} onClick={removeAttached}>Remove</button>
        </div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <input
          style={{ ...m.input, flex: 1 }}
          placeholder="Name, phone number or email"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") doSearch(); }}
          autoFocus
        />
        <button style={{ ...m.primary, width: 110, marginTop: 0 }} onClick={doSearch} disabled={loading}>
          {loading ? "…" : "Find"}
        </button>
      </div>
      <div style={{ marginTop: 12, maxHeight: 340, overflow: "auto" }}>
        {!loading && searched && rows.length === 0 && (
          <div style={{ color: "var(--ink-3)", padding: 16, textAlign: "center" }}>
            <div>No customer found</div>
            {/* The point of failure IS the moment to offer creation — the cashier searched, the
                customer isn't on file, and the sale is waiting. Android puts create on the same
                dialog for the same reason. The searched term prefills the phone field. */}
            <button style={{ ...m.primary, marginTop: 12 }} onClick={() => setCreating(true)}>
              ＋ Add this customer
            </button>
          </div>
        )}
        {rows.map((c, i) => (
          <div
            key={pick(c, "id", "customer_id") || i}
            onClick={() => choose(c)}
            style={{ display: "flex", justifyContent: "space-between", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 10, marginBottom: 8, cursor: "pointer" }}
          >
            <div>
              <div style={{ fontWeight: 500 }}>{nameOf(c)}</div>
              {emailOf(c) && <div style={{ fontSize: 12, color: "var(--ink-2)" }}>{emailOf(c)}</div>}
            </div>
            <span style={{ color: "var(--ink-2)" }}>{phoneOf(c)}</span>
          </div>
        ))}
      </div>
    </Modal>
  );
}
